import { style, styleVariants, globalStyle } from '@vanilla-extract/css';
import { vars } from '../../theme';
import { reducedMotion } from '../../theme/media';

export type CheckboxSize = 'sm' | 'md' | 'lg';

// ============================================
// ROOT (label wrapper — implicit association)
// ============================================

export const root = style({
  display: 'inline-flex',
  alignItems: 'center',
  gap: vars.spacing.gap.sm,
  cursor: 'pointer',
  userSelect: 'none',
  fontFamily: vars.typography.fontFamily.primary,
  color: vars.typography.textColor.primary,
});

export const rootDisabled = style({
  cursor: 'not-allowed',
  opacity: 0.5,
});

// ============================================
// CONTROL (native input + visual box)
// ============================================

export const controlWrapper = style({
  position: 'relative',
  display: 'inline-flex',
  flexShrink: 0,
});

export const nativeInput = style({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  margin: 0,
  opacity: 0,
  cursor: 'inherit',
});

const boxBase = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box' as const,
  background: vars.form.fieldGradient,
  border: `1px solid ${vars.surface.border.primary}`,
  borderRadius: vars.spacing.borderRadius.sm,
  boxShadow: vars.form.fieldInsetShadow,
  color: vars.typography.textColor.primary,
  transition: `background ${vars.animation.duration.fast} ${vars.animation.easing.easeOut}, border-color ${vars.animation.duration.fast} ${vars.animation.easing.easeOut}`,
  '@media': { [reducedMotion]: { transition: 'none' } },
} as const;

export const box = styleVariants({
  sm: { ...boxBase, width: '16px', height: '16px' },
  md: { ...boxBase, width: '18px', height: '18px' },
  lg: { ...boxBase, width: '22px', height: '22px' },
});

// Check mark (svg) and indeterminate dash — hidden by default, revealed by state
export const checkMark = style({
  width: '70%',
  height: '70%',
  opacity: 0,
  transition: `opacity ${vars.animation.duration.fast} ${vars.animation.easing.easeOut}`,
  '@media': { [reducedMotion]: { transition: 'none' } },
});

export const dashMark = style({
  position: 'absolute',
  width: '56%',
  height: '2px',
  borderRadius: '1px',
  background: 'currentColor',
  opacity: 0,
});

// ============================================
// LABEL TEXT
// ============================================

export const labelText = styleVariants({
  sm: { fontSize: vars.typography.fontSize.xs },
  md: { fontSize: vars.typography.fontSize.sm },
  lg: { fontSize: vars.typography.fontSize.base },
});

// ============================================
// STATE SELECTORS (element-scoped, mirrors Input.css pattern)
// ============================================

// checked → filled box + check visible
globalStyle(`${controlWrapper} input:checked + span`, {
  background: vars.bright.primary.default,
  borderColor: vars.bright.primary.default,
});
globalStyle(`${controlWrapper} input:checked + span svg`, {
  opacity: 1,
});

// indeterminate overrides checked → show dash, hide check
globalStyle(`${controlWrapper} input:indeterminate + span`, {
  background: vars.bright.primary.default,
  borderColor: vars.bright.primary.default,
});
globalStyle(`${controlWrapper} input:indeterminate + span svg`, {
  opacity: 0,
});
globalStyle(`${controlWrapper} input:indeterminate + span > span`, {
  opacity: 1,
});

// hover (only when enabled)
globalStyle(`${controlWrapper}:hover input:not(:disabled) + span`, {
  borderColor: vars.surface.border.accent,
});

// focus-visible ring on the visual box
globalStyle(`${controlWrapper} input:focus-visible + span`, {
  outline: `${vars.focus.ring.width} ${vars.focus.ring.style} ${vars.focus.ring.color}`,
  outlineOffset: vars.focus.ring.offset,
  borderColor: vars.bright.primary.default,
});
